import { useEffect, useState, useCallback } from "react";
import { AlertTriangle, MapPin, CalendarDays, Trophy, Send, CalendarCheck, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls = "w-full h-12 border border-border px-4 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-flame focus:border-transparent";

const OfferForm = ({ lead, onDone }) => {
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/offer`, { amount: amount ? parseFloat(amount) : null, message });
      toast.success("Offer submitted");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit offer");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-4 space-y-3" data-testid={`offer-form-${lead.id}`}>
      <div className="grid sm:grid-cols-3 gap-3">
        <input data-testid={`offer-amount-${lead.id}`} type="number" min="0" placeholder="Amount $ (optional)"
          value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} />
        <input data-testid={`offer-message-${lead.id}`} placeholder="Your offer message (min. 5 chars)"
          value={message} onChange={(e) => setMessage(e.target.value)} className={`${inputCls} sm:col-span-2`} />
      </div>
      <button data-testid={`offer-submit-${lead.id}`} disabled={busy || message.trim().length < 5} onClick={submit}
        className="flex items-center gap-2 bg-flame hover:bg-flame-dark disabled:opacity-40 text-white font-heading font-bold uppercase tracking-wider text-xs px-6 py-3 transition-colors">
        <Send size={14} /> Submit Offer
      </button>
    </div>
  );
};

const AppointmentActions = ({ lead, onDone }) => {
  const [suggesting, setSuggesting] = useState(false);
  const [date, setDate] = useState("");
  const [win, setWin] = useState("");

  const act = async (payload) => {
    try {
      await api.post(`/leads/${lead.id}/appointment`, payload);
      toast.success(payload.action === "accept" ? "Appointment time accepted" : "Alternative time suggested");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    }
  };

  if (lead.appointment_action) {
    return (
      <p className="text-xs text-slate-500 mt-3">
        {lead.appointment_action === "accept" ? "✓ You accepted the requested appointment time." : "✓ You suggested an alternative time."}
      </p>
    );
  }

  return (
    <div className="mt-4 border-t border-border pt-4">
      <p className="text-[11px] font-bold uppercase tracking-widest text-navy mb-2">Preferred appointment: {lead.request.preferred_date} {lead.request.preferred_time_window || ""}</p>
      <div className="flex flex-wrap gap-2">
        <button data-testid={`appt-accept-${lead.id}`} onClick={() => act({ action: "accept" })}
          className="flex items-center gap-2 bg-navy hover:bg-navy-light text-white text-[11px] font-heading font-bold uppercase tracking-wider px-4 py-2.5 transition-colors">
          <CalendarCheck size={13} /> Accept Time
        </button>
        <button data-testid={`appt-suggest-toggle-${lead.id}`} onClick={() => setSuggesting(!suggesting)}
          className="flex items-center gap-2 border border-border hover:border-navy text-navy text-[11px] font-heading font-bold uppercase tracking-wider px-4 py-2.5 transition-colors">
          <CalendarClock size={13} /> Suggest Another Time
        </button>
      </div>
      {suggesting && (
        <div className="flex flex-wrap gap-2 mt-3">
          <input data-testid={`appt-date-${lead.id}`} type="date" min={new Date().toISOString().split("T")[0]}
            value={date} onChange={(e) => setDate(e.target.value)} className="h-11 border border-border px-3 text-sm" />
          <select data-testid={`appt-window-${lead.id}`} value={win} onChange={(e) => setWin(e.target.value)} className="h-11 border border-border px-3 text-sm bg-white">
            <option value="">Time window</option>
            {["Morning (8am–12pm)", "Afternoon (12pm–4pm)", "Evening (4pm–7pm)", "Flexible"].map((t) => <option key={t}>{t}</option>)}
          </select>
          <button data-testid={`appt-suggest-submit-${lead.id}`} disabled={!date}
            onClick={() => act({ action: "suggest", suggested_date: date, suggested_time_window: win || null })}
            className="bg-flame hover:bg-flame-dark disabled:opacity-40 text-white text-[11px] font-heading font-bold uppercase tracking-wider px-5 py-2.5 transition-colors">
            Send Suggestion
          </button>
        </div>
      )}
    </div>
  );
};

export const LeadInbox = () => {
  const [leads, setLeads] = useState(null);

  const load = useCallback(() => {
    api.get("/leads/my").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (leads === null) return <p className="text-slate-400 text-sm mt-10">Loading leads…</p>;

  return (
    <div className="mt-10" data-testid="lead-inbox">
      <h2 className="font-heading font-black text-navy text-2xl tracking-tight mb-1">Lead Inbox</h2>
      <p className="text-slate-500 text-sm mb-6">Matched project opportunities in your service area. Emergency leads appear first.</p>
      {leads.length === 0 ? (
        <div data-testid="lead-inbox-empty" className="bg-white border border-border p-10 text-center">
          <p className="font-heading font-bold text-navy">No leads yet</p>
          <p className="text-slate-500 text-sm mt-2">You'll see matched requests here as customers post projects in your categories and cities.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {leads.map((l) => (
            <div key={l.id} data-testid={`lead-card-${l.id}`}
              className={`bg-white border p-6 ${l.is_emergency ? "border-red-500 border-l-4" : "border-border"}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-heading font-black text-navy text-xl tracking-tight">{l.request.category_name}</h3>
                    {l.is_emergency && (
                      <span data-testid={`lead-emergency-badge-${l.id}`} className="flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                        <AlertTriangle size={11} /> Emergency
                      </span>
                    )}
                    {l.request.booked_to_me && (
                      <span className="flex items-center gap-1 bg-green-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                        <Trophy size={11} /> You were selected
                      </span>
                    )}
                  </div>
                  <p className="text-slate-500 text-sm mt-2 max-w-xl">{l.request.description}</p>
                </div>
                <span className="text-[11px] font-bold uppercase tracking-widest border border-border px-3 py-1 text-slate-500">
                  {l.request.status.replace(/_/g, " ")}
                </span>
              </div>
              <div className="flex items-center gap-5 mt-4 text-xs text-slate-400 flex-wrap">
                <span className="flex items-center gap-1.5"><MapPin size={13} className="text-flame" /> {l.request.city}, {l.request.zip_code}</span>
                <span>{l.request.budget_range}</span>
                <span className="capitalize">{l.request.estimate_method} estimate</span>
                {l.request.preferred_date && (
                  <span className="flex items-center gap-1.5"><CalendarDays size={13} className="text-flame" /> {l.request.preferred_date}</span>
                )}
              </div>

              {l.request.booked_to_me && l.request.contact_name && (
                <div className="mt-4 bg-green-50 border border-green-200 px-4 py-3 text-sm text-navy" data-testid={`lead-contact-${l.id}`}>
                  <strong>Customer contact:</strong> {l.request.contact_name} · {l.request.contact_phone} · {l.request.contact_email} (prefers {l.request.contact_preference})
                </div>
              )}

              {l.my_offer ? (
                <p className="text-xs text-slate-500 mt-4 border-t border-border pt-3" data-testid={`lead-offer-status-${l.id}`}>
                  ✓ Offer submitted{l.my_offer.amount != null ? ` — $${l.my_offer.amount.toLocaleString()}` : ""} · Status: {l.my_offer.status}
                </p>
              ) : l.status === "declined" ? (
                <p className="text-xs text-slate-400 mt-4">You declined this lead.</p>
              ) : ["booked", "completed", "cancelled", "expired"].includes(l.request.status) ? (
                <p className="text-xs text-slate-400 mt-4 border-t border-border pt-3">This request is closed.</p>
              ) : l.offers_full ? (
                <p className="text-xs text-slate-400 mt-4 border-t border-border pt-3" data-testid={`lead-cap-${l.id}`}>
                  This opportunity has reached its offer limit.
                </p>
              ) : (
                <>
                  <OfferForm lead={l} onDone={load} />
                  {!l.is_emergency && l.request.preferred_date && <AppointmentActions lead={l} onDone={load} />}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadInbox;
